import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const message = data.get('message') as string;
    if (message && message.length > 500) {
      return;
    }

    setStatus('sending');

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value as string);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d77rf0r2hsar0k5m0030', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-28 md:py-36 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-stone-400 mb-4">
            <span className="w-4 h-px bg-stone-300" />
            Contact
            <span className="w-4 h-px bg-stone-300" />
          </span>
          <h2 className="text-3xl md:text-5xl font-thin text-stone-900 leading-tight">
            Get in Touch
          </h2>
          <p className="mt-4 text-stone-500 text-base max-w-md mx-auto">
            Have a question or want to learn more about our products? We'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact info */}
          <div className="flex flex-col gap-8">
            <div>
              <h3 className="text-lg font-semibold text-stone-900 mb-6">Contact Information</h3>
              <div className="flex flex-col gap-5">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-stone-100 shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-stone-600 text-base" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-widest text-stone-400 block mb-1">Address</span>
                    <p className="text-stone-700 text-sm leading-relaxed">
                      Loka 16<br />
                      Logatec, 1370<br />
                      Slovenia
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-stone-100 shrink-0 mt-0.5">
                    <i className="ri-mail-line text-stone-600 text-base" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-widest text-stone-400 block mb-1">Email</span>
                    <p className="text-stone-700 text-sm">contact@tuldoo.com</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-stone-100 shrink-0 mt-0.5">
                    <i className="ri-phone-line text-stone-600 text-base" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-widest text-stone-400 block mb-1">Phone</span>
                    <p className="text-stone-700 text-sm">+1 6626768248</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map placeholder */}
            <div className="rounded-2xl overflow-hidden h-48 bg-stone-100 flex items-center justify-center">
              <iframe
                title="TUL d.o.o. location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2773.0!2d14.2281!3d45.9178!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x477a9f1b2e2e2e2e%3A0x0!2sLoka%2016%2C%201370%20Logatec%2C%20Slovenia!5e0!3m2!1sen!2ssi!4v1680000000000!5m2!1sen!2ssi"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div>
            <h3 className="text-lg font-semibold text-stone-900 mb-6">Send a Message</h3>
            <form
              data-readdy-form
              id="tul-contact-form"
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold uppercase tracking-widest text-stone-400">Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Your name"
                    className="border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-stone-400 transition-colors bg-stone-50"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold uppercase tracking-widest text-stone-400">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-stone-400 transition-colors bg-stone-50"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase tracking-widest text-stone-400">Subject</label>
                <input
                  type="text"
                  name="subject"
                  required
                  placeholder="How can we help?"
                  className="border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-stone-400 transition-colors bg-stone-50"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase tracking-widest text-stone-400">
                  Message <span className="normal-case font-normal text-stone-300">({charCount}/500)</span>
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="Write your message here..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-stone-400 transition-colors bg-stone-50 resize-none"
                />
                {charCount >= 500 && (
                  <p className="text-xs text-red-400">Maximum 500 characters reached.</p>
                )}
              </div>

              <button
                type="submit"
                disabled={status === 'sending' || charCount > 500}
                className="bg-stone-900 text-white text-sm font-medium rounded-lg px-6 py-3.5 hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'sending' ? 'Sending...' : 'Send Message'}
              </button>

              {status === 'success' && (
                <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3">
                  <i className="ri-checkbox-circle-line" />
                  Message sent successfully. We'll get back to you soon.
                </div>
              )}
              {status === 'error' && (
                <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                  <i className="ri-error-warning-line" />
                  Something went wrong. Please try again.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
